import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  cancel_button,
  EDITOR_SEARCH_DEBOUNCE_MS as TAG_SEARCH_DEBOUNCE_MS,
  EDITOR_SEARCH_MIN_LENGTH as TAG_SEARCH_MIN_LENGTH,
  popup_padding,
  save_button,
} from 'src/config/config'
import {
  ICustomTagResult,
  useAddPortfolioTagToSolutionMutation,
  useCreatePortfolioTagMutation,
  useSearchCustomTagsLazyQuery,
} from 'src/graphql/manual/portfolioCustomTagHooks'
import { useDebouncedValue } from 'src/hooks/useDebouncedValue'
import { showToast } from 'src/store/actions/toastAction'

interface Props {
  solutionId?: number
  solutionDocumentId?: string
  portfolioId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  /** When true, only creates the tag in the portfolio repo (does not attach to a solution). */
  createOnly?: boolean
  isSharedPortfolio?: boolean
}

function tagAlreadyOnSolution(t: ICustomTagResult | null | undefined, solutionId: number, solutionDocumentId: string) {
  if (!t) return false
  if (solutionId > 0 && t.solutionIds?.includes(solutionId)) return true
  if (solutionDocumentId && t.solutionDocumentIds?.includes(solutionDocumentId)) return true
  return false
}

function sortSuggestions(keyword: string, tags: ICustomTagResult[]) {
  const kw = keyword.trim().toLowerCase()
  return [...tags].sort((a, b) => {
    const an = (a.name || '').toLowerCase()
    const bn = (b.name || '').toLowerCase()
    if (!kw) return an.localeCompare(bn)
    const aPref = an.startsWith(kw) ? 0 : 1
    const bPref = bn.startsWith(kw) ? 0 : 1
    if (aPref !== bPref) return aPref - bPref
    return an.localeCompare(bn)
  })
}

export const AddCustomTagModal: React.FC<Props> = ({
  solutionId = 0,
  solutionDocumentId = '',
  portfolioId = '',
  isOpen,
  onClose,
  onSuccess,
  createOnly = false,
  isSharedPortfolio,
}) => {
  const dispatch: Dispatch<any> = useDispatch()
  const [inputValue, setInputValue] = useState('')
  const [selectedSuggestion, setSelectedSuggestion] = useState<ICustomTagResult | undefined>(undefined)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const suppressNextFocus = useRef(false)

  const [searchTags, { data: searchData, loading: searchLoading }] = useSearchCustomTagsLazyQuery({
    fetchPolicy: 'network-only',
  })
  const [createPortfolioTag, { loading: creating }] = useCreatePortfolioTagMutation()
  const [addPortfolioTagToSolution, { loading: adding }] = useAddPortfolioTagToSolutionMutation()
  const isLoading = creating || adding

  const trimmedInput = inputValue.trim()
  const debouncedSearchKeyword = useDebouncedValue(trimmedInput, TAG_SEARCH_DEBOUNCE_MS)
  const searchKeyword = debouncedSearchKeyword.length >= TAG_SEARCH_MIN_LENGTH ? debouncedSearchKeyword : ''
  const isSearchPending = trimmedInput.length >= TAG_SEARCH_MIN_LENGTH && trimmedInput !== debouncedSearchKeyword
  const isSearchLoading = Boolean(isSearchPending || (searchLoading && searchKeyword))

  const searchResults = useMemo(() => {
    if (trimmedInput.length < TAG_SEARCH_MIN_LENGTH) return []
    if (trimmedInput !== debouncedSearchKeyword) return []
    return (searchData?.searchCustomTags || []).filter(Boolean) as ICustomTagResult[]
  }, [trimmedInput, debouncedSearchKeyword, searchData?.searchCustomTags])

  const trimmedLower = trimmedInput.toLowerCase()

  const tagMatchingNameExact = useMemo(() => {
    const selectedMatchesInput =
      selectedSuggestion && (selectedSuggestion.name || '').trim().toLowerCase() === trimmedLower
        ? selectedSuggestion
        : undefined
    if (selectedMatchesInput) return selectedMatchesInput
    return trimmedLower ? searchResults.find((t) => (t?.name || '').trim().toLowerCase() === trimmedLower) : undefined
  }, [searchResults, trimmedLower, selectedSuggestion])

  const alreadyOnThisSolution =
    !createOnly && !!tagMatchingNameExact && tagAlreadyOnSolution(tagMatchingNameExact, solutionId, solutionDocumentId)

  const tagAlreadyExistsInPortfolio = createOnly && !!tagMatchingNameExact

  const suggestionsAssignable = useMemo(
    () =>
      // Sidebar create-only: existing tags are not selectable (attach-to-solution flow only).
      createOnly ? [] : searchResults.filter((t) => !tagAlreadyOnSolution(t, solutionId, solutionDocumentId)),
    [searchResults, solutionId, solutionDocumentId, createOnly],
  )

  const canCreateNewName = trimmedInput.length >= TAG_SEARCH_MIN_LENGTH && !tagMatchingNameExact

  const suggestionsToShow = useMemo(() => {
    if (trimmedInput.length < TAG_SEARCH_MIN_LENGTH) return []
    if (trimmedInput !== debouncedSearchKeyword) return []
    return sortSuggestions(trimmedInput, suggestionsAssignable)
  }, [trimmedInput, debouncedSearchKeyword, suggestionsAssignable])

  const hasMinSearchInput = trimmedInput.length >= TAG_SEARCH_MIN_LENGTH
  const showDropdown =
    hasMinSearchInput && showSuggestions && (isSearchLoading || canCreateNewName || suggestionsToShow.length > 0)
  const showButtons = !showDropdown

  const fetchSuggestions = useCallback(
    (keyword: string) => {
      searchTags({
        variables: {
          keyword,
          portfolioId: portfolioId,
          isSharedPortfolio:
            typeof isSharedPortfolio === 'boolean' ? isSharedPortfolio : portfolioId !== '' ? true : false,
        },
      })
    },
    [searchTags, portfolioId, isSharedPortfolio],
  )

  useEffect(() => {
    if (!isOpen || !searchKeyword) return
    fetchSuggestions(searchKeyword)
  }, [isOpen, searchKeyword, fetchSuggestions])

  useEffect(() => {
    if (isOpen) {
      setInputValue('')
      setShowSuggestions(false)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    setSelectedSuggestion(undefined)
    const q = val.trim()
    if (q.length < TAG_SEARCH_MIN_LENGTH) {
      setShowSuggestions(false)
      return
    }
    setShowSuggestions(true)
  }

  const handleInputFocus = () => {
    if (suppressNextFocus.current) {
      suppressNextFocus.current = false
      return
    }
    if (inputValue.trim().length < TAG_SEARCH_MIN_LENGTH) return
    setShowSuggestions(true)
  }

  const handleSelectSuggestion = (tag: ICustomTagResult) => {
    suppressNextFocus.current = true
    setInputValue(tag.name || '')
    setSelectedSuggestion(tag)
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const handleSelectCreateOption = () => {
    suppressNextFocus.current = true
    setSelectedSuggestion(undefined)
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  const addToSolutionBaseVariables = () => ({
    solutionId: solutionId > 0 ? solutionId : null,
    solutionDocumentId: solutionDocumentId || null,
  })

  const handlePrimaryAdd = async () => {
    if (!trimmedInput || isLoading || alreadyOnThisSolution) return

    if (trimmedInput.length > 50) {
      dispatch(showToast('Custom tag name cannot exceed 50 characters.', 'info'))
      return
    }
    try {
      if (createOnly) {
        if (!portfolioId) {
          dispatch(showToast('Portfolio is required to create a custom tag.', 'info'))
          return
        }
        if (tagMatchingNameExact?.id) {
          dispatch(showToast('This tag already exists.', 'info'))
          onSuccess()
          onClose()
          return
        }
        await createPortfolioTag({
          variables: { name: trimmedInput, portfolioId },
        })
        dispatch(showToast('Custom tag created successfully.', 'success'))
        onSuccess()
        onClose()
        return
      }

      if (tagMatchingNameExact?.id) {
        await addPortfolioTagToSolution({
          variables: { tagDocumentId: tagMatchingNameExact.id, ...addToSolutionBaseVariables() },
        })
      } else {
        const variablesData = { name: trimmedInput, portfolioId: portfolioId }
        const { data } = await createPortfolioTag({
          variables: variablesData,
        })
        if (data?.createPortfolioTag?.id) {
          await addPortfolioTagToSolution({
            variables: {
              tagDocumentId: data.createPortfolioTag.id,
              ...addToSolutionBaseVariables(),
            },
          })
        }
      }
      dispatch(showToast('Custom tag added successfully.', 'success'))
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add tag'
      dispatch(showToast(message, 'info'))
    }
  }

  const primaryDisabled = !trimmedInput || alreadyOnThisSolution || tagAlreadyExistsInPortfolio || isLoading

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div ref={containerRef} className="relative mx-4 w-full max-w-[600px]" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute md:top-[-10px] top-[-12px] md:right-[-9px] right-[-9px] z-20 flex h-6 w-6 items-center justify-center rounded-full bg-[#011D58] text-white hover:opacity-90"
          aria-label="Close"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1 1L9 9M9 1L1 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div
          className={`${popup_padding} min-h-[269px] md:min-h-[317px] relative w-full rounded-[8px] bg-white shadow-[-16px_17px_37px_0px_rgba(0,0,0,0.1)]`}
          /* style={{ minHeight: 416 }} */
          role="dialog"
          aria-labelledby="add-custom-tag-title"
          aria-modal="true"
        >
          <h2
            id="add-custom-tag-title"
            className="md:mb-[10px] mb-[8px] font-secondary text-[24px] font-bold leading-[1.5] text-[#333333]"
          >
            {createOnly ? 'Create Custom Tag' : 'Add Custom Tag'}
          </h2>

          <div className="h-px w-full bg-[#DDDDDD]" />
          <p className="text-[16px] font-secondary text-darkgray mt-6">
            {createOnly
              ? 'Create custom tags for this portfolio so you can easily group, search, and filter solutions.'
              : "Add an existing custom tag to a solution, or create a new one if a suitable match isn't available. Custom tags help you group, search, and filter your portfolio solutions."}
          </p>

          <div className="mt-6">
            <div className="relative">
              <div className="relative flex h-[40px] w-full items-center overflow-hidden rounded-[4px] border border-[#DDDDDD] bg-white">
                <input
                  id="custom-tag-input"
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  placeholder=""
                  className="h-full w-full bg-transparent px-[12px] font-primary text-[15px] font-medium text-[#011D58] outline-none"
                  autoComplete="off"
                />
              </div>

              {showDropdown && (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+4px)] z-[50] flex max-h-[180px] md:max-h-[224px] flex-col overflow-y-auto overscroll-contain rounded-[4px] border border-[#DDDDDD] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)] [-webkit-overflow-scrolling:touch]"
                  role="listbox"
                >
                  {isSearchLoading && (
                    <div className="px-[12px] py-[10px] font-secondary text-[14px] text-[#767676]">Loading…</div>
                  )}
                  {!isSearchLoading && canCreateNewName && (
                    <button
                      type="button"
                      role="option"
                      className="w-full border-0 bg-transparent px-[12px] py-[10px] text-left font-secondary text-[14px] font-medium text-[#011D58] hover:bg-[#F5F7FA]"
                      onClick={handleSelectCreateOption}
                      disabled={isLoading}
                    >
                      Add custom tag &ldquo;{trimmedInput}&rdquo;
                    </button>
                  )}
                  {!isSearchLoading &&
                    suggestionsToShow.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        role="option"
                        className="w-full border-0 bg-transparent px-[12px] py-[10px] text-left font-secondary text-[14px] font-medium text-[#011D58] hover:bg-[#F5F7FA] disabled:opacity-50"
                        onClick={() => handleSelectSuggestion(tag)}
                        disabled={isLoading}
                      >
                        {tag.name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {alreadyOnThisSolution && (
              <p className="mt-[6px] font-secondary text-[13px] text-[#767676]">
                This tag is already on this solution.
              </p>
            )}
            {tagAlreadyExistsInPortfolio && (
              <p className="mt-[6px] font-secondary text-[13px] text-[#767676]">This tag already exists.</p>
            )}
          </div>

          {showButtons && (
            <div className="mt-6 flex w-full min-w-0 flex-row flex-wrap items-center md:gap-[16px] gap-[8px] font-sans">
              <button
                className={`${save_button}`}
                type="button"
                onClick={() => void handlePrimaryAdd()}
                disabled={primaryDisabled}
                style={{ backgroundColor: primaryDisabled ? '#011D58' : '#011D58' }}
              >
                <p className="text-white">{createOnly ? 'Create Custom Tag' : 'Add Custom Tag'}</p>
              </button>

              <button className={`${cancel_button}`} type="button" onClick={onClose}>
                <p className="text-[#333333]">Cancel</p>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
